import type { ReactNode } from 'react';
import { ErrorState as DsErrorState, RowSkeleton, TeachingEmpty, type ErrorStateProps as DsErrorStateProps } from '@zagru/design-system';
import { describeError, humanizeFieldPath, useT, type DescribeErrorOptions, type UserFacingError } from '@zagru/platform';

type PassThrough = Omit<DsErrorStateProps, 'title' | 'description' | 'detail' | 'errors' | 'code' | 'correlationId' | 'status' | 'retryLabel'>;

export interface ErrorStateProps extends PassThrough {
  /** Anything a query, mutation or render threw. It is translated, never printed. */
  error: unknown;
  /** Plural noun phrase for what failed ("outbound orders"). Drives "Outbound orders couldn't be loaded". */
  subject?: string;
  /** Reads (`load`, default) or writes (`save`). */
  operation?: DescribeErrorOptions['action'];
  /** Overrides the derived title, e.g. for a page that knows better words. */
  title?: ReactNode;
}

/**
 * The only way the app renders a thrown value (ADR-0023 §error architecture).
 *
 * `describeError` turns a `ZagruProblem` (or anything else) into operator language: what could not happen, what it
 * means, whether trying again helps. Schema paths, exception text and 5xx server prose never reach the screen; the
 * correlation id is available behind "Technical details". Load failures render calm (`neutral`); failed writes
 * render `critical` and list field messages.
 */
export function ErrorState({ error, subject, operation = 'load', title, ...rest }: ErrorStateProps) {
  const t = useT();
  if (error === null || error === undefined) return null;
  const described = describeError(error, t, { subject, action: operation });
  return <FailurePanel failure={described} title={title ?? described.title} tone={operation === 'save' ? 'critical' : 'neutral'} {...rest} />;
}

export interface FailurePanelProps extends PassThrough {
  failure: UserFacingError;
  title?: ReactNode;
  description?: ReactNode;
  secondaryAction?: ReactNode;
  /** Screen-supplied labels for the server's field names, e.g. `{ dockDoorId: 'Dock door' }`. */
  fieldLabels?: Record<string, string>;
}

/**
 * Presents an already-described failure; retry is offered only when trying again can help.
 *
 * Field names arrive as the request body spelled them (`lines[0].quantityExpected`). The screen's own label wins;
 * failing that the path is worded, because asking an operator to read a JSON path is asking them to read the API.
 */
export function FailurePanel({ failure, title, description, onRetry, secondaryAction, action, fieldLabels, ...rest }: FailurePanelProps) {
  const t = useT();
  const fieldErrors = failure.fieldErrors.map((e) => ({
    ...e,
    field: (e.field ? fieldLabels?.[e.field] : undefined) ?? humanizeFieldPath(e.field),
  }));
  return (
    <DsErrorState
      title={title ?? failure.title}
      description={description ?? failure.description}
      errors={fieldErrors.length > 0 ? fieldErrors : undefined}
      code={failure.code}
      correlationId={failure.diagnosticId}
      status={failure.status}
      onRetry={failure.retryable || failure.kind === 'unknown' ? onRetry : undefined}
      retryLabel={t('failure.tryAgain')}
      technicalLabel={t('failure.technicalDetails')}
      diagnosticLabel={t('failure.diagnosticId')}
      copyLabel={t('failure.copyDiagnosticId')}
      copiedLabel={t('failure.diagnosticIdCopied')}
      action={
        action || secondaryAction ? (
          <>
            {action}
            {secondaryAction}
          </>
        ) : undefined
      }
      {...rest}
    />
  );
}

export interface AsyncSectionProps {
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  /** Plural noun phrase for the failure copy. */
  subject?: string;
  /** True when the request succeeded but returned nothing. */
  empty?: boolean;
  emptyState?: ReactNode;
  skeleton?: ReactNode;
  children: ReactNode;
  /** `compact` for widgets and rail sections: a failure takes one line instead of a panel. */
  variant?: 'inline' | 'compact';
  /** Announces the resolved result to assistive technology. */
  liveLabel?: string;
}

/** Wraps a data-bound section with its states so no surface can ship without them — and so a failure stays local. */
export function AsyncSection({ loading, error, onRetry, subject, empty, emptyState, skeleton, children, variant = 'inline', liveLabel }: AsyncSectionProps) {
  const t = useT();
  let body: ReactNode;
  if (error) body = <ErrorState error={error} subject={subject} onRetry={onRetry} variant={variant} />;
  else if (loading) body = skeleton ?? <RowSkeleton />;
  else if (empty)
    body = emptyState ?? <TeachingEmpty title={t('states.empty.title')} description={t('states.empty.description')} size="sm" />;
  else body = children;
  if (!liveLabel) return <>{body}</>;
  return (
    <div role="status" aria-live="polite" aria-label={liveLabel} className="contents">
      {body}
    </div>
  );
}
